import type { WebsiteShow } from './website';

export interface BandsintownStatus {
  lastSyncedAt: string;
  importedCount: number;
}

export const bandsintownStatusKey = 'artistos-bandsintown-status';

export function getBandsintownStatus(): BandsintownStatus | null {
  try {
    const stored = window.localStorage.getItem(bandsintownStatusKey);
    return stored ? (JSON.parse(stored) as BandsintownStatus) : null;
  } catch {
    return null;
  }
}

export function saveBandsintownStatus(status: BandsintownStatus) {
  try {
    window.localStorage.setItem(bandsintownStatusKey, JSON.stringify(status));
  } catch {
    // Status is a convenience only; ignore storage failures.
  }
}

interface BandsintownEvent {
  id: string;
  datetime: string;
  url: string;
  venue: { name?: string; city?: string; region?: string; country?: string };
  offers?: { type?: string; url?: string; status?: string }[];
}

export interface BandsintownSyncResult {
  shows: WebsiteShow[];
  error?: string;
}

/** Fetches upcoming dates from the public Bandsintown artist API. */
export async function fetchBandsintownShows(artistName: string, appId: string): Promise<BandsintownSyncResult> {
  const artist = artistName.trim();
  const app = appId.trim();
  if (!artist || !app) {
    return { shows: [], error: 'Add your Bandsintown artist name and app ID first.' };
  }

  try {
    const endpoint = `https://rest.bandsintown.com/artists/${encodeURIComponent(artist)}/events?app_id=${encodeURIComponent(app)}&date=upcoming`;
    const response = await fetch(endpoint);
    if (!response.ok) {
      return { shows: [], error: `Bandsintown returned ${response.status}. Check the artist name and app ID.` };
    }
    const payload: unknown = await response.json();
    if (!Array.isArray(payload)) {
      return { shows: [], error: 'No upcoming dates found for that artist name.' };
    }
    return { shows: (payload as BandsintownEvent[]).map(toWebsiteShow) };
  } catch {
    return { shows: [], error: 'Could not reach Bandsintown. Check your connection and try again.' };
  }
}

/**
 * Keeps manually added shows that Bandsintown does not know about, while
 * dropping any hand-entered date that Bandsintown has since published — so the
 * same night never appears twice.
 */
export function mergeBandsintownShows(existing: WebsiteShow[], incoming: WebsiteShow[]): WebsiteShow[] {
  const fingerprint = (show: WebsiteShow) => `${show.date}|${show.venue.trim().toLowerCase()}`;
  const synced = new Set(incoming.map(fingerprint));
  const manual = existing.filter((show) => !show.id.startsWith('bit-') && !synced.has(fingerprint(show)));
  return [...incoming, ...manual];
}

function toWebsiteShow(event: BandsintownEvent): WebsiteShow {
  const ticketOffer = event.offers?.find((offer) => offer.type === 'Tickets');
  const soldOut = ticketOffer?.status === 'sold out';
  const cityParts = [event.venue?.city, event.venue?.region || event.venue?.country].filter(Boolean);

  return {
    id: `bit-${event.id}`,
    date: formatEventDate(event.datetime),
    venue: event.venue?.name ?? 'Venue TBA',
    city: cityParts.join(', '),
    ticketUrl: ticketOffer?.url ?? event.url,
    presaleNote: soldOut ? 'Sold out' : 'Tickets available',
    status: soldOut ? 'sold-out' : ticketOffer ? 'on-sale' : 'coming-soon',
  };
}

function formatEventDate(datetime: string): string {
  const date = new Date(datetime);
  if (Number.isNaN(date.getTime())) return datetime;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}
